import React, { useState, useRef, useEffect } from 'react';
import { Briefcase } from 'lucide-react';
import api from '../../api/api';

// Comprehensive job categories across all industries
const CATEGORY_SUGGESTIONS = [
  // Technology & IT
  'Software Engineering',
  'Web Development',
  'Mobile Development',
  'Frontend Development',
  'Backend Development',
  'Full Stack Development',
  'DevOps Engineering',
  'Data Science',
  'Data Engineering',
  'Data Analytics',
  'Machine Learning',
  'Artificial Intelligence',
  'Cloud Computing',
  'Cybersecurity',
  'Information Security',
  'Network Engineering',
  'Systems Administration',
  'Database Administration',
  'IT Support',
  'Technical Support',
  'Game Development',
  'Blockchain Development',
  'IoT Development',
  
  // Design & Creative
  'UI/UX Design',
  'Graphic Design',
  'Product Design',
  'Visual Design',
  'Motion Graphics',
  'Animation',
  'Video Editing',
  'Photography',
  'Interior Design',
  'Fashion Design',
  'Industrial Design',
  'Creative Direction',
  
  // Business & Management
  'Product Management',
  'Project Management',
  'Program Management',
  'Business Analysis',
  'Business Development',
  'Strategy & Planning',
  'Operations Management',
  'Supply Chain Management',
  'Quality Assurance',
  'Quality Control',
  'Process Improvement',
  'Consulting',
  'Management Consulting',
  
  // Marketing & Sales
  'Digital Marketing',
  'Content Marketing',
  'Social Media Marketing',
  'SEO/SEM',
  'Email Marketing',
  'Performance Marketing',
  'Brand Management',
  'Marketing Strategy',
  'Sales',
  'Business Development',
  'Account Management',
  'Customer Success',
  'Inside Sales',
  'Field Sales',
  'Retail Sales',
  
  // Finance & Accounting
  'Finance',
  'Accounting',
  'Financial Analysis',
  'Investment Banking',
  'Corporate Finance',
  'Wealth Management',
  'Risk Management',
  'Audit',
  'Tax',
  'Payroll',
  'Bookkeeping',
  'Treasury',
  
  // Human Resources
  'Human Resources',
  'Recruitment',
  'Talent Acquisition',
  'HR Operations',
  'Compensation & Benefits',
  'Training & Development',
  'Employee Relations',
  'HR Business Partner',
  
  // Healthcare & Medical
  'Healthcare',
  'Nursing',
  'Medical',
  'Pharmaceutical',
  'Clinical Research',
  'Medical Devices',
  'Biotechnology',
  'Healthcare Administration',
  'Physical Therapy',
  'Occupational Therapy',
  'Dentistry',
  'Veterinary',
  'Mental Health',
  'Public Health',
  
  // Education & Training
  'Education',
  'Teaching',
  'Training',
  'Curriculum Development',
  'Instructional Design',
  'Academic Research',
  'Student Services',
  'Educational Technology',
  'Corporate Training',
  
  // Engineering (Non-IT)
  'Mechanical Engineering',
  'Electrical Engineering',
  'Civil Engineering',
  'Chemical Engineering',
  'Aerospace Engineering',
  'Automotive Engineering',
  'Environmental Engineering',
  'Biomedical Engineering',
  'Manufacturing Engineering',
  'Quality Engineering',
  'Process Engineering',
  'Safety Engineering',
  
  // Construction & Real Estate
  'Construction Management',
  'Architecture',
  'Civil Engineering',
  'Project Engineering',
  'Site Management',
  'Real Estate',
  'Property Management',
  'Facilities Management',
  'Urban Planning',
  
  // Legal & Compliance
  'Legal',
  'Corporate Law',
  'Contract Management',
  'Compliance',
  'Regulatory Affairs',
  'Intellectual Property',
  'Legal Research',
  'Paralegal',
  
  // Customer Service & Support
  'Customer Support',
  'Customer Service',
  'Call Center',
  'Technical Support',
  'Help Desk',
  'Client Services',
  'Customer Experience',
  
  // Media & Communications
  'Content Writing',
  'Copywriting',
  'Technical Writing',
  'Journalism',
  'Public Relations',
  'Communications',
  'Media Planning',
  'Broadcasting',
  'Publishing',
  
  // Retail & E-commerce
  'Retail Management',
  'Store Management',
  'E-commerce',
  'Merchandising',
  'Buying',
  'Inventory Management',
  'Retail Operations',
  
  // Hospitality & Tourism
  'Hospitality',
  'Hotel Management',
  'Restaurant Management',
  'Food & Beverage',
  'Tourism',
  'Event Management',
  'Catering',
  'Front Office',
  
  // Logistics & Transportation
  'Logistics',
  'Supply Chain',
  'Warehouse Management',
  'Transportation',
  'Distribution',
  'Procurement',
  'Inventory Control',
  'Fleet Management',
  
  // Manufacturing & Production
  'Manufacturing',
  'Production Management',
  'Production Planning',
  'Assembly',
  'Quality Control',
  'Maintenance',
  'Industrial Engineering',
  
  // Agriculture & Food
  'Agriculture',
  'Agronomy',
  'Food Science',
  'Food Processing',
  'Agricultural Engineering',
  'Horticulture',
  
  // Energy & Utilities
  'Energy',
  'Renewable Energy',
  'Oil & Gas',
  'Power Generation',
  'Utilities',
  'Energy Management',
  
  // Government & Non-Profit
  'Government',
  'Public Administration',
  'Non-Profit',
  'Social Work',
  'Community Development',
  'Policy Analysis',
  
  // Research & Development
  'Research',
  'R&D',
  'Scientific Research',
  'Product Development',
  'Innovation',
  'Laboratory',
  
  // Other Professional Services
  'Administrative',
  'Office Management',
  'Executive Assistant',
  'Personal Assistant',
  'Reception',
  'Data Entry',
  'Transcription',
  'Translation',
  'Interpretation',
];

/**
 * Category Selector with Autocomplete
 * @param {String} selectedCategory - Currently selected category
 * @param {Function} onChange - Callback when category changes
 * @param {Boolean} required - Whether category is required
 * @param {String} placeholder - Placeholder text
 */
const CategorySelector = ({ 
  selectedCategory = '', 
  onChange, 
  required = false,
  placeholder = 'e.g., Software Engineering'
}) => {
  const [inputValue, setInputValue] = useState(selectedCategory);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load custom categories from backend on mount
  useEffect(() => {
    const loadCustomCategories = async () => {
      try {
        const response = await api.get('/custom-data');
        if (response.data && response.data.customCategories) {
          setCustomCategories(response.data.customCategories);
        }
      } catch (err) {
        console.log('Could not load custom categories:', err);
      }
    };
    loadCustomCategories();
  }, []);

  // Sync input value with selectedCategory prop
  useEffect(() => {
    setInputValue(selectedCategory);
  }, [selectedCategory]);

  // Filter suggestions based on input (including custom categories)
  useEffect(() => {
    const allCategories = [...CATEGORY_SUGGESTIONS, ...customCategories];
    
    if (inputValue.trim()) {
      const filtered = allCategories.filter(cat =>
        cat.toLowerCase().includes(inputValue.toLowerCase())
      );

      setFilteredSuggestions(filtered.slice(0, 10));
    } else {
      setFilteredSuggestions(allCategories.slice(0, 10));
    }
  }, [inputValue, customCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCategory = async (category) => {
    setInputValue(category);
    onChange(category);
    setShowSuggestions(false);
    inputRef.current?.blur();
    
    // Save custom category if not in predefined list
    const isCustom = !CATEGORY_SUGGESTIONS.some(c => c.toLowerCase() === category.toLowerCase());
    
    if (isCustom && category.trim()) {
      try {
        await api.post('/custom-data', {
          type: 'category',
          value: category.trim()
        });
        if (!customCategories.some(c => c.toLowerCase() === category.toLowerCase())) {
          setCustomCategories(prev => [...prev, category.trim()].sort());
        }
      } catch (err) {
        console.log('Could not save custom category:', err);
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    onChange(value);
  };
  
  const handleBlur = async () => {
    // Save custom category when user leaves input
    if (inputValue && inputValue.trim()) {
      const isCustom = !CATEGORY_SUGGESTIONS.some(c => c.toLowerCase() === inputValue.toLowerCase());
      
      if (isCustom) {
        try {
          await api.post('/custom-data', {
            type: 'category',
            value: inputValue.trim()
          });
          if (!customCategories.some(c => c.toLowerCase() === inputValue.toLowerCase())) {
            setCustomCategories(prev => [...prev, inputValue.trim()].sort());
          }
        } catch (err) {
          console.log('Could not save custom category:', err);
        }
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      onChange(inputValue.trim());
      setShowSuggestions(false);
    }
  };

  return (
    <div className="w-full">
      {/* Input Field */}
      <div className="relative">
        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
        />

        {/* Autocomplete Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-xs text-gray-600 font-medium">
                {inputValue.trim() ? 'Matching Categories' : 'Popular Categories'}
              </p>
            </div>
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectCategory(suggestion)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
              >
                <Briefcase size={16} className="text-gray-400" />
                <span>{suggestion}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500 mt-1">
        Start typing to see suggestions or enter your own
      </p>
    </div>
  );
};

export default CategorySelector;
